import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  getJob,
  sendQuote,
  sendQueries,
  rerunJob,
  updateJob,
  issueUploadLink,
  buildReport,
  buildWorkpaper,
  resolveDocsGap,
  deleteJob,
  getDocumentDownload,
  listStaff,
  reassignJob,
} from '../api/jobs';
import type { QueryEmailInput } from '../api/jobs';
import { UnauthorizedError } from '../api/client';
import type { JobDetail as JobDetailData, ClarificationQuery, Job, ValuationInputs, DocumentItem, StaffMember } from '../types';
import { Spinner, ErrorBanner, StatusBadge, toMessage } from '../components/common';
import { firstName, money } from '../format';

export function JobDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<JobDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Editable working copy of the latest query set.
  const [queryDraft, setQueryDraft] = useState<ClarificationQuery[]>([]);

  const [editing, setEditing] = useState(false);
  const [detailsChanged, setDetailsChanged] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getJob(id);
      setData(res);
      const latest = [...(res.queries ?? [])].sort((a, b) => b.version - a.version)[0];
      setQueryDraft(latest ? latest.queries.map((q) => ({ ...q })) : []);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        navigate('/signin', { replace: true });
        return;
      }
      setError(toMessage(err));
    }
  }, [id, navigate]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  async function run(action: string, fn: () => Promise<unknown>, successMsg: string) {
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      await load();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        navigate('/signin', { replace: true });
        return;
      }
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }


  if (error && !data) {
    return (
      <>
        <ErrorBanner message={error} />
        <Link to="/jobs">Back to jobs</Link>
      </>
    );
  }
  if (!data) return <Spinner label="Loading job." />;

  const { job, documents, messages } = data;
  const sortedMessages = [...(messages ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  // Progressive disclosure: reveal each section as it becomes relevant to the
  // job's stage, rather than front-loading everything at Draft.
  const STATUS_RANK: Record<string, number> = {
    DRAFT: 0, QUOTE_DRAFTED: 0, QUOTE_SENT: 1,
    PROPOSAL_SIGNED: 2, AWAITING_DOCS: 2, DOCS_RECEIVED: 2,
    REVIEW: 3, QUERIES_DRAFTED: 3, QUERIES_SENT: 4, QUERIES_ANSWERED: 4, CLOSED: 4,
    REPORTING: 5, DELIVERED: 6,
  };
  const rank = STATUS_RANK[job.status] ?? 0;
  const showDocuments = rank >= 2 || (documents?.length ?? 0) > 0; // from Proposal signed onward
  // The client's documents and the app's own output are different things and were
  // interleaved in one table, sorted by nothing in particular. On Jay's Unit Trust
  // that put four generated workbooks among five real uploads.
  const received = (documents ?? [])
    .filter((d) => d.source !== 'generated')
    .sort((a, b) => +new Date(a.receivedAt) - +new Date(b.receivedAt));
  const generated = (documents ?? [])
    .filter((d) => d.source === 'generated')
    .sort((a, b) => +new Date(b.receivedAt) - +new Date(a.receivedAt));
  const showQueries =
    job.status === 'REVIEW' ||
    job.status === 'QUERIES_SENT' ||
    job.status === 'QUERIES_ANSWERED' ||
    queryDraft.length > 0;
  const showThread = sortedMessages.length > 0; // once the first email (the quote) exists

  return (
    <>
      <div className="toolbar">
        <div>
          <h1>{job.entityName}</h1>
          <div className="muted">
            {job.engagementType} &middot; <StatusBadge status={job.status} />
            {job.quoteSentAt ? (
              <span className="quote-sent-at">
                {' '}quote sent {new Date(job.quoteSentAt).toLocaleString('en-AU', {
                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            ) : null}
          </div>
        </div>
        <Link className="back-link" to="/jobs">
          ← All jobs
        </Link>
      </div>
      {editing ? (
        <EditJob
          job={job}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            setDetailsChanged(true);
            await load();
            setNotice('Details updated. Re-send the quote to send the corrected version to the client.');
          }}
        />
      ) : null}

      <PhaseStepper status={job.status} />

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <div className="notice">{notice}</div> : null}

      <NextStep status={job.status} />
      <OutstandingPanel job={job} onEditYears={() => setEditing(true)} onChanged={load} />
      <XeroNote integrations={job.integrations} />

      <div className="btn-row" style={{ marginBottom: '1.25rem' }}>
        {job.status === 'DRAFT' ? (
          <button onClick={() => run('quote', () => sendQuote(id), 'Quote sent to the client.')} disabled={busy !== null}>
            {busy === 'quote' ? 'Working.' : 'Send quote'}
          </button>
        ) : null}
        {/*
          Re-sending was previously gated on detailsChanged, so it only appeared
          after you had edited something. A client who deleted or never received the
          proposal left the valuer with no way to send it again, which is the most
          ordinary support request there is. Available on any quoted engagement now,
          and each send issues a fresh link and retires the previous one.
        */}
        {job.quoteSentAt && job.status === 'QUOTE_SENT' ? (
          <button
            className={detailsChanged ? undefined : 'btn-secondary'}
            title={
              detailsChanged
                ? 'Send the corrected proposal. The previous link stops working.'
                : 'Email the proposal again with a fresh signing link. The previous link stops working.'
            }
            onClick={() =>
              run(
                'quote',
                async () => { await sendQuote(id); setDetailsChanged(false); },
                detailsChanged ? 'Corrected quote re-sent.' : 'Proposal re-sent with a fresh signing link.',
              )
            }
            disabled={busy !== null}
          >
            {busy === 'quote' ? 'Working.' : 'Re-send quote'}
          </button>
        ) : null}
        <button
          className="btn-secondary"
          onClick={() => run('rerun', () => rerunJob(id), 'Re-run triggered.')}
          disabled={busy !== null}
        >
          {busy === 'rerun' ? 'Working.' : 'Re-run'}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Details</h3>
          <button className="btn-secondary btn-mini-2" onClick={() => setEditing(true)} disabled={busy !== null}>
            Edit details
          </button>
        </div>
        <div className="grid-2">
          <Field label="ABN" value={job.abn} />
          <Field label="Industry" value={job.industry} />
          <Field label="Financial years" value={job.financialYears?.join(', ')} />
          <Field
            label="Fee quoted ex GST"
            value={job.feeQuotedExGst != null ? `$${job.feeQuotedExGst.toLocaleString()}` : undefined}
          />
          <Field label="Contact" value={job.contact?.name} />
          <Field label="Contact email" value={job.contact?.email} />
          <Field label="Contact phone" value={job.contact?.phone} />
          <Field label="Created by" value={job.createdBy} />
          <Field label="Owner" value={job.ownerName || (job.ownerSub ? 'unnamed staff member' : 'unassigned')} />
        </div>
        {job.docRequirements && job.docRequirements.length > 0 ? (
          <>
            <h3 style={{ marginTop: '1rem' }}>Document requirements</h3>
            <ul className="req-list">
              {job.docRequirements.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <OwnerPanel job={job} onChanged={load} />

      {job.errors && job.errors.length > 0 ? (
        <div className="card">
          <h3>Errors</h3>
          {job.errors.map((e, i) => (
            <div key={i} className="file-row">
              <span className="dot error" />
              <strong style={{ minWidth: 130 }}>{e.step}</strong>
              <span className="muted">{e.message}</span>
            </div>
          ))}
        </div>
      ) : null}

      {showDocuments ? (
        <>
          <div className="card">
            <h3>Documents from the client</h3>
            {/* The integrity check is the app's verdict on THESE files, so it reads
                above them. It used to sit three panels lower, past the client forms
                and the clarification email, where nobody connected it to the table. */}
            {rank >= 3 ? <WorkpaperIntegrityBanner job={job} inline /> : null}
            <DocumentTable jobId={job.jobId} docs={received} empty="No documents received yet." />
          </div>
        </>
      ) : null}

      {rank >= 2 ? <ChaseBanner job={job} /> : null}

      {job.uploadUrl ? <UploadLinkPanel job={job} busy={busy} run={run} /> : null}

      {rank >= 2 ? (
        <ClientFormsPanel
          job={job}
          data={data}
          busy={busy}
          documents={documents}
          draft={queryDraft}
          latestSet={[...(data.queries ?? [])].sort((a, b) => a.version - b.version).slice(-1)[0]}
          showQueries={showQueries}
          onSendQueries={(email) =>
            run('queries', () => sendQueries(id, email), 'Clarification email sent to the client.')
          }
        />
      ) : null}

      {rank >= 3 ? <ReportPanel job={job} onChanged={load} /> : null}

      {/* Below the panel that produces them, because that is the order they happen in. */}
      {showDocuments ? (
        <>
          {/*
            The app's own output, kept apart from the client's.
            Every rebuild writes a new file, so on a job that has been through a few
            passes these outnumbered the real documents and buried them. Newest first,
            and the current one is labelled so nobody downloads a stale workbook.
          */}
          {generated.length > 0 ? (
            <div className="card">
              <h3>Generated documents</h3>
              <p className="muted" style={{ marginTop: 0 }}>
                Produced by the app. Every rebuild is kept, newest first. The one marked
                current is the most recent.
              </p>
              <DocumentTable jobId={job.jobId} docs={generated} showCurrent empty="" />
            </div>
          ) : null}
        </>
      ) : null}

      {showThread ? (
      <div className="card">
        {/*
          No Refresh button here on purpose. ms365-reply-poll already sweeps the
          mailbox every 5 minutes and files replies onto the job by Graph
          conversationId, so the thread catches up on its own; a button would only
          duplicate a job that is already being done.
        */}
        <h3>Email thread</h3>
        {/* Three kinds of message look alike in a flat list, and it matters which
            is which: a template the app sent on its own, something a person wrote,
            and the client's own words. Colour plus a stated legend, because colour
            on its own is not an explanation. */}
        <div className="thread-legend">
          <span><i className="key app" />Sent by the app</span>
          <span><i className="key user" />Written by you</span>
          <span><i className="key client" />From the client</span>
        </div>
        {sortedMessages.length > 0 ? (
          sortedMessages.map((m) => (
            <div
              key={m.msgId}
              className={`msg ${m.direction} ${m.direction === 'inbound' ? 'from-client' : m.origin === 'user' ? 'from-user' : 'from-app'}`}
            >
              <div className="meta">
                <span className="msg-tag">
                  {m.direction === 'inbound' ? 'Client' : m.origin === 'user' ? 'You' : 'App'}
                </span>
                <span>{m.direction === 'inbound' ? 'From' : 'To'}: {m.direction === 'inbound' ? m.from : m.to}</span>
                {m.sentByFallback && m.sentAsLogin ? (
                  <span className="muted" title="The engagement owner has not connected Microsoft 365, so another staff member's connection sent this.">
                    {' '}&middot; sent via {m.sentAsLogin}
                  </span>
                ) : null}
                <span>{new Date(m.createdAt).toLocaleString()}</span>
                <span>{m.status}</span>
              </div>
              <strong>{m.subject}</strong>
              <pre>{m.bodyText || '(no plain-text body)'}</pre>
            </div>
          ))
        ) : (
          <p className="muted">No messages yet.</p>
        )}
      </div>
      ) : null}

      {/*
        Last thing on the page, by design. Deleting a valuation job is not something
        anyone should reach for on the way to something else.
      */}
      <DangerZone job={job} />
    </>
  );
}

// Upload-link box, driven by the link's ACTUAL validity. Shows the current link
// and its validity. The Re-issue button is only clickable once the link has
// expired, in which case the box turns amber.
/**
 * The filename in the Documents list, as a download.
 *
 * The bytes are not proxied through the API: the endpoint mints a presigned S3
 * GET valid for a few minutes and we navigate to it. That covers a 25 MB client
 * upload as well as a 95 KB generated workpaper, and keeps large files off the
 * API Gateway response path. The signed URL carries Content-Disposition:
 * attachment, so an untrusted client upload never renders in the app's origin.
 */
/**
 * One documents table, used for both the client's uploads and the app's output.
 *
 * `showCurrent` marks the newest file of each name, because the generated section
 * deliberately keeps every rebuild and four rows called "Valuation Workpaper.xlsx"
 * are otherwise indistinguishable.
 */
function DocumentTable({
  jobId,
  docs,
  showCurrent = false,
  empty,
}: {
  jobId: string;
  docs: DocumentItem[];
  showCurrent?: boolean;
  empty: string;
}) {
  if (!docs.length) return empty ? <p className="muted">{empty}</p> : null;
  const currentIds = new Set<string>();
  if (showCurrent) {
    const newestByName = new Map<string, DocumentItem>();
    for (const d of docs) {
      const seen = newestByName.get(d.filename);
      if (!seen || new Date(d.receivedAt) > new Date(seen.receivedAt)) newestByName.set(d.filename, d);
    }
    for (const d of newestByName.values()) currentIds.add(d.docId);
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>File</th>
            {showCurrent ? <th></th> : <th>Client says it is</th>}
            <th>Kind</th>
            <th>Parse</th>
            <th>{showCurrent ? 'Built' : 'Received'}</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.docId}>
              <td><DocumentLink jobId={jobId} doc={d} /></td>
              {showCurrent ? (
                <td>{currentIds.has(d.docId) ? <span className="badge-current">current</span> : null}</td>
              ) : (
                <td className="muted">{d.requirement || '\u2014'}</td>
              )}
              <td>{d.kind}</td>
              <td>
                <span className={`dot ${d.parseStatus === 'error' ? 'error' : d.parseStatus === 'parsed' ? 'ok' : 'pending'}`} />
                {d.parseStatus}
              </td>
              <td className="muted">{new Date(d.receivedAt).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Permanent deletion, at the very bottom of the page.
 *
 * Typed confirmation rather than a plain OK: this removes the client's financial
 * statements and everything they told us, and it cannot be undone. The same word is
 * checked again on the server.
 */
function DangerZone({ job }: { job: Job }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const armed = typed.trim().toLowerCase() === 'delete';

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const res = await deleteJob(job.jobId, typed.trim());
      navigate(
        `/jobs?deleted=${encodeURIComponent(res.entityName)}&items=${res.deleted.jobItems}&files=${res.deleted.s3Objects}`,
      );
    } catch (err) {
      setError(toMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="card danger-zone">
      <h3>Delete this job</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Removes the job, the client&rsquo;s documents, their answers and every generated
        file. This cannot be undone.
      </p>
      <button type="button" className="btn-danger" onClick={() => setOpen(true)}>
        Delete {job.entityName}
      </button>

      {open ? (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Confirm deletion">
          <div className="modal">
            <h3>This cannot be undone</h3>
            <p>
              Deleting <strong>{job.entityName}</strong> permanently removes:
            </p>
            <ul>
              <li>Every document the client uploaded, and every version of them</li>
              <li>Their general information answers and their answers to the clarification queries</li>
              <li>Every workpaper and report the app has generated</li>
              <li>The whole email thread, and the client&rsquo;s upload and answer links</li>
            </ul>
            <p className="muted">
              Not removed: emails already sitting in a mailbox, and any Xero invoice, which
              has to be voided in Xero by hand.
            </p>
            {error ? <ErrorBanner message={error} /> : null}
            <label className="field">
              Type <strong>delete</strong> to confirm
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                autoComplete="off"
                placeholder="delete"
              />
            </label>
            <div className="btn-row">
              <button
                type="button"
                className="btn-danger"
                disabled={!armed || busy}
                onClick={run}
              >
                {busy ? 'Deleting.' : 'Delete permanently'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  setTyped('');
                  setError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DocumentLink({ jobId, doc }: { jobId: string; doc: DocumentItem }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A rejected upload was deleted from S3 by docs-record; there is nothing to fetch.
  if (doc.parseStatus === 'error' && !doc.s3Key) return <>{doc.filename}</>;

  async function download() {
    setBusy(true);
    setErr(null);
    try {
      const res = await getDocumentDownload(jobId, doc.docId);
      if (
        res.macroEnabled &&
        !window.confirm(
          `${res.filename} is a macro-enabled workbook supplied by the client. ` +
            'Open it with macros disabled. Continue?',
        )
      ) {
        return;
      }
      // Navigate to the signed URL. The `download` attribute is ignored
      // cross-origin, which is why the URL sets Content-Disposition server-side.
      const a = document.createElement('a');
      a.href = res.url;
      a.rel = 'noopener';
      a.click();
    } catch (e) {
      setErr(toMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="linklike" onClick={download} disabled={busy}>
        {doc.filename}
      </button>
      {doc.macroEnabled ? (
        <span className="muted" title="Macro-enabled workbook"> (macros)</span>
      ) : null}
      {err ? <div className="error">{err}</div> : null}
    </>
  );
}


/**
 * Move an engagement to another staff member.
 *
 * Not a label change: Job.ownerSub decides whose Microsoft 365 connection sends the
 * client email, and each connection resolves its own send-as address, so handing a
 * job over changes what the client sees on everything sent from here on and where
 * their replies land. Hence the confirm, and hence the warning when the new owner
 * has not connected a mailbox yet.
 */
function OwnerPanel({ job, onChanged }: { job: Job; onChanged: () => void | Promise<void> }) {
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || staff) return;
    listStaff()
      .then((r) => setStaff(r.staff))
      .catch((e) => setError(toMessage(e)));
  }, [open, staff]);

  const target = staff?.find((s) => s.sub === choice);

  async function save() {
    if (!target) return;
    const warning = target.connected
      ? ''
      : `\n\n${target.name} has not connected Microsoft 365. Until they do, this job's ` +
        'email will keep going out from another connected staff member.';
    if (
      !window.confirm(
        `Hand this engagement to ${target.name}?\n\nClient email from here on will be sent ` +
          `from their mailbox, and replies will land there.${warning}`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await reassignJob(job.jobId, target.sub);
      setOpen(false);
      setChoice('');
      await onChanged();
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Owner</h3>
        <button className="btn-secondary btn-mini-2" onClick={() => setOpen(!open)} disabled={busy}>
          {open ? 'Cancel' : 'Reassign'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        <strong>{job.ownerName || (job.ownerSub ? 'An unnamed staff member' : 'Nobody')}</strong> owns
        this engagement. Client email is sent from their connected mailbox.
      </p>
      {error ? <ErrorBanner message={error} /> : null}
      {open ? (
        staff === null ? (
          <Spinner label="Loading staff." />
        ) : (
          <div className="form-row">
            <select value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="">Hand over to...</option>
              {staff
                .filter((s) => s.sub !== job.ownerSub)
                .map((s) => (
                  <option key={s.sub} value={s.sub}>
                    {s.name}
                    {s.connected ? '' : ' (no mailbox connected)'}
                  </option>
                ))}
            </select>
            <button onClick={save} disabled={!choice || busy}>
              {busy ? 'Working.' : 'Hand over'}
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}

function UploadLinkPanel({
  job,
  busy,
  run,
}: {
  job: JobDetailData['job'];
  busy: string | null;
  run: (a: string, fn: () => Promise<unknown>, msg: string) => Promise<void>;
}) {
  const valid = Boolean(job.uploadLinkExpiresAt && new Date(job.uploadLinkExpiresAt).getTime() > Date.now());
  const expires = job.uploadLinkExpiresAt
    ? new Date(job.uploadLinkExpiresAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!job.uploadUrl) return;
    try {
      await navigator.clipboard.writeText(job.uploadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked; ignore */
    }
  };
  return (
    <div className={`upload-link-box ${valid ? 'active' : 'expired'}`}>
      <span className="ulb-chip">Upload link</span>
      <div className="ulb-body">
        {job.uploadUrl ? (
          <span className="ulb-url-row">
            <a href={job.uploadUrl} target="_blank" rel="noreferrer" className="ulb-url">{job.uploadUrl}</a>
            <button
              type="button"
              className="ulb-copy"
              title={copied ? 'Copied' : 'Copy link'}
              aria-label="Copy upload link"
              onClick={copy}
            >
              {copied ? '✓' : '⧉'}
            </button>
          </span>
        ) : (
          <span className="muted">No active upload link for this engagement.</span>
        )}
        <span className="ulb-expiry">
          {valid ? `Valid until ${expires}` : expires ? `Expired ${expires}` : 'Not yet issued'}
        </span>
      </div>
      <div className="ulb-actions">
        <button
          className="btn-secondary"
          disabled={busy !== null || valid}
          title={valid ? 'The current link is still valid. Renew becomes available once it expires.' : 'Generate a new 30-day upload link. Copy it with the icon to share with the client.'}
          onClick={() => run('renew-link', () => issueUploadLink(job.jobId), 'A new 30-day upload link was generated.')}
        >
          {busy === 'renew-link' ? 'Working.' : 'Renew Link'}
        </button>
      </div>
    </div>
  );
}

/**
 * The two client portals, which are the whole point of not sending spreadsheets any
 * more. Both existed as API routes and public pages before this panel did, which
 * meant a valuer had no way to start either one from the app.
 *
 *   General information  the questionnaire that used to be an Excel attachment
 *                        emailed back and forth. Available as soon as the client
 *                        has signed, because it does not depend on the financials.
 *   Clarification        the queries, answered in the browser instead of by reply
 *                        email. Only available once the drafter has produced a set.
 *
 * Each row sends the client a link and then shows it, so it can be re-copied or
 * re-sent without digging through the thread.
 */
/**
 * The general information questionnaire, which replaces the Excel one.
 *
 * The clarification queries used to sit here as a second row, beside a separate
 * "Clarification email" panel lower down: two controls, two emails to the client,
 * and nothing explaining the difference. They are now one thing, in the
 * Clarification panel, which sends the questions and the link to answer them in a
 * single message.
 */
function ClientFormsPanel({
  job,
  data,
  busy,
  documents,
  draft,
  latestSet,
  showQueries,
  onSendQueries,
}: {
  job: JobDetailData['job'];
  data: JobDetailData;
  busy: string | null;
  documents: JobDetailData['documents'];
  draft: ClarificationQuery[];
  latestSet?: JobDetailData['queries'][number];
  showQueries: boolean;
  onSendQueries: (email: QueryEmailInput) => void;
}) {
  // The General information row has gone. It used to carry a Send to client button
  // that somebody had to remember to press, and it was the second email and second
  // link the client had to keep track of. The questionnaire is now one of the two
  // tabs behind the Collect information link they get on signing, so its state is
  // reported up with the rest of that step rather than sitting here as a task.
  const intakeDone = Boolean(data.intake?.submittedAt);
  return (
    <section className="card">
      <h3>Clarification queries</h3>
      <p className="muted" style={{ marginTop: '-0.35rem', fontSize: '0.85rem' }}>
        A link the client answers themselves. Nothing is attached and nothing has to be
        typed back in: what they submit goes straight onto the Queries tab of the
        workpaper.
      </p>
      {intakeDone ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          General information was answered
          {data.intake?.submittedByName ? ` by ${data.intake.submittedByName}` : ''} on the
          Collect information link.
        </p>
      ) : null}
      {showQueries ? (
        <QueriesEmail
          job={job}
          documents={documents}
          draft={draft}
          latestSet={latestSet}
          busy={busy}
          onSend={onSendQueries}
        />
      ) : null}
    </section>
  );
}

function ChaseBanner({ job }: { job: JobDetailData['job'] }) {
  const sent = job.docChasesSent ?? [];
  if (job.docChaseEscalatedAt) {
    return (
      <div className="next-step escalated" style={{ display: 'block' }}>
        <strong>{job.contact.name} has stopped responding.</strong> The app chased{' '}
        {sent.length} time{sent.length === 1 ? '' : 's'} for the outstanding documents
        and has stopped. This one needs a call rather than another email.
      </div>
    );
  }
  if (sent.length === 0) return null;
  const last = new Date(sent[sent.length - 1]).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short',
  });
  return (
    <div className="next-step waiting" style={{ display: 'block' }}>
      Waiting on the client. The app has chased {sent.length} time
      {sent.length === 1 ? '' : 's'} for the outstanding documents, most recently on{' '}
      {last}. Nothing needed from you yet.
    </div>
  );
}

// Edit the core job details, then re-issue the quote. Uses the existing update
// endpoint; Send quote afterwards regenerates the quote from the saved data.
function EditJob({ job, onClose, onSaved }: { job: JobDetailData['job']; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    entityName: job.entityName,
    abn: job.abn ?? '',
    industry: job.industry ?? '',
    engagementType: job.engagementType,
    financialYears: (job.financialYears ?? []).join(', '),
    contactName: job.contact?.name ?? '',
    contactEmail: job.contact?.email ?? '',
    contactPhone: job.contact?.phone ?? '',
    feeQuotedExGst: job.feeQuotedExGst != null ? String(job.feeQuotedExGst) : '',
    docRequirements: (job.docRequirements ?? []).join('\n'),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await updateJob(job.jobId, {
        entityName: f.entityName.trim(),
        abn: f.abn.trim() || undefined,
        industry: f.industry.trim() || undefined,
        engagementType: f.engagementType.trim(),
        financialYears: f.financialYears.split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        contact: { name: f.contactName.trim(), email: f.contactEmail.trim(), phone: f.contactPhone.trim() || undefined },
        feeQuotedExGst: f.feeQuotedExGst ? Number(f.feeQuotedExGst) : undefined,
        docRequirements: f.docRequirements.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      onSaved();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <h2>Edit details</h2>
        <form onSubmit={save}>
          <div className="grid-2">
            <label>Entity name<input value={f.entityName} onChange={set('entityName')} required /></label>
            <label>ABN<input value={f.abn} onChange={set('abn')} /></label>
            <label>Industry<input value={f.industry} onChange={set('industry')} /></label>
            <label>Engagement type<input value={f.engagementType} onChange={set('engagementType')} required /></label>
            <label>Contact name<input value={f.contactName} onChange={set('contactName')} required /></label>
            <label>Contact email<input value={f.contactEmail} onChange={set('contactEmail')} required /></label>
            <label>Contact phone<input value={f.contactPhone} onChange={set('contactPhone')} /></label>
            <label>Fee ex GST (AUD)<input type="number" value={f.feeQuotedExGst} onChange={set('feeQuotedExGst')} /></label>
          </div>
          <label style={{ display: 'block', marginTop: '0.6rem' }}>Financial years (comma separated)
            <input value={f.financialYears} onChange={set('financialYears')} style={{ width: '100%' }} />
          </label>
          <label style={{ display: 'block', marginTop: '0.6rem' }}>Document requirements (one per line)
            <textarea value={f.docRequirements} onChange={set('docRequirements')} rows={3} style={{ width: '100%' }} />
          </label>
          {error ? <p style={{ color: 'var(--err)' }}>{error}</p> : null}
          <div className="btn-row" style={{ marginTop: '0.8rem' }}>
            <button type="submit" disabled={busy}>{busy ? 'Saving.' : 'Save details'}</button>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Read-only four-phase progress bar mapping persisted job statuses to the stages
// a staff member thinks in. Done phases show a tick, the current phase is filled
// navy, future phases are greyed. Status is NOT user-editable; it advances via
// the pipeline and explicit actions only.
function PhaseStepper({ status }: { status: string }) {
  const phaseOf: Record<string, number> = {
    DRAFT: 1, QUOTE_SENT: 1,
    PROPOSAL_SIGNED: 2, AWAITING_DOCS: 2, DOCS_RECEIVED: 2,
    REVIEW: 3,
    QUERIES_SENT: 4, QUERIES_ANSWERED: 4,
    REPORTING: 5, DELIVERED: 6,
    // legacy statuses still on old jobs
    QUOTE_DRAFTED: 1, QUERIES_DRAFTED: 3, CLOSED: 4,
  };
  const active = phaseOf[status] ?? 1;
  const phases = [
    { n: 1, label: 'Quote', sub: 'Proposal sent' },
    { n: 2, label: 'Collect information', sub: 'Questionnaire, documents' },
    { n: 3, label: 'Review', sub: 'P&L, queries' },
    { n: 4, label: 'Queries', sub: 'Sent to client' },
    { n: 5, label: 'Report', sub: 'Delivered' },
  ];
  return (
    <div className="job-stepper" aria-label="Job progress">
      {phases.map((p) => (
        <div
          key={p.n}
          className={`jstep${p.n < active ? ' done' : ''}${p.n === active ? ' active' : ''}`}
        >
          <span className="jstep-dot">{p.n < active ? '✓' : p.n}</span>
          <div className="jstep-label">{p.label}</div>
          <div className="jstep-sub">{p.sub}</div>
        </div>
      ))}
    </div>
  );
}

// Compact Xero indicator. The Integrations panel was removed, so this surfaces
// only what needs staff attention: a contact or invoice that is pending (Xero not
// connected) or errored. Stays hidden when both are fine or absent.
function XeroNote({ integrations }: { integrations?: JobDetailData['job']['integrations'] }) {
  const contact = integrations?.xero;
  const invoice = integrations?.xeroInvoice;
  const problems: string[] = [];
  if (contact?.status === 'error') problems.push(`Xero contact failed: ${contact.error ?? 'error'}`);
  else if (contact?.status === 'pending') problems.push('Xero contact pending: Xero is not connected yet.');
  if (invoice?.status === 'error') problems.push(`Xero invoice failed: ${invoice.error ?? 'error'}`);
  else if (invoice?.status === 'pending') problems.push('Xero invoice pending: Xero is not connected. Raise the invoice manually once connected.');
  if (problems.length === 0) return null;
  return (
    <div className="next-step waiting" style={{ display: 'block' }}>
      <span className="next-step-label">Xero</span>
      <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
        {problems.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Seed the editor from the AI-drafted queries so staff edit rather than start blank.
function draftToHtml(job: JobDetailData['job'], draft: ClarificationQuery[]): string {
  const first = escHtml(firstName(job.contact.name));
  const greeting = `<p>Hi ${first},</p>`;
  const closing = '<p>Kind regards,<br>WA Business Valuations</p>';
  if (!draft.length) {
    return `${greeting}<p>Following our review of your documents, we have a few clarifications before we can proceed:</p><ul><li></li></ul>${closing}`;
  }
  const items = draft.map((q) => `<li>${escHtml(q.query || q.area)}</li>`).join('');
  return `${greeting}<p>Following our review of your documents, we have the following clarification queries:</p><ol>${items}</ol>${closing}`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

// Basic HTML email editor (bold / italic / underline / bullets / numbering) plus
// attachments (local files and existing job documents). Replaces the structured
// clarification-queries editor.
function QueriesEmail({
  job,
  documents,
  draft,
  latestSet,
  busy,
  onSend,
}: {
  job: JobDetailData['job'];
  documents: JobDetailData['documents'];
  draft: ClarificationQuery[];
  latestSet?: JobDetailData['queries'][number];
  busy: string | null;
  onSend: (email: QueryEmailInput) => void;
}) {
  // Open. It was collapsed by default, which buried the Send button below eight
  // paragraphs of questions and made the header the only obvious thing to click.
  // The questions ARE the content of this row; hiding them hid the action too.
  // Open while there is a decision to make, collapsed once it is made. Eight
  // paragraphs of questions the valuer has already read and sent are reference
  // material, not the thing they came to the page for.
  const [open, setOpen] = useState(
    () => !['QUERIES_SENT', 'QUERIES_ANSWERED', 'REPORTING', 'DELIVERED'].includes(job.status),
  );
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(`Clarification queries for ${job.entityName}`);
  const [files, setFiles] = useState<File[]>([]);
  const [docIds, setDocIds] = useState<Set<string>>(new Set());
  const editorRef = useRef<HTMLDivElement>(null);
  const seeded = useRef(false);

  // `editing` belongs in the deps: the editor is only mounted once you press Edit
  // the email, so on the first pass editorRef is null and this did nothing, and it
  // never re-ran because job and draft had not changed. Pressing Edit therefore
  // opened an empty box with the queries nowhere in sight.
  useEffect(() => {
    if (editorRef.current && !seeded.current) {
      editorRef.current.innerHTML = draftToHtml(job, draft);
      if (draft.length) seeded.current = true; // lock once real queries arrive
    }
  }, [job, draft, editing]);

  const exec = (cmd: string) => {
    document.execCommand(cmd, false);
    editorRef.current?.focus();
  };

  async function send() {
    // In view mode the editor is not rendered, so editorRef is null and this used
    // to post an EMPTY body: the client would have received a clarification email
    // with no questions in it. Fall back to composing from the draft, which is
    // what the editor would have been seeded with anyway.
    const html = editorRef.current?.innerHTML || draftToHtml(job, draft);
    const attachments = await Promise.all(
      files.map(async (f) => ({ filename: f.name, contentType: f.type || undefined, contentBase64: await fileToBase64(f) })),
    );
    onSend({ subject, html, attachments, attachDocIds: [...docIds] });
  }

  const attachable = (documents ?? []).filter((d) => d.parseStatus !== 'error' || d.source === 'generated');

  // .upload-link-box carries no background of its own: it comes from .active or
  // .expired. Without one of those this row rendered bare and stopped looking
  // like the General information row beside it. Amber until the questions have
  // gone, blue once they have, matching every other row on the page.
  // A prose reply must not sit unread in the database.
  const answeredNote = latestSet?.note;
  const sent =
    Boolean(job.queriesFormUrl) ||
    ['QUERIES_SENT', 'QUERIES_ANSWERED', 'REPORTING', 'DELIVERED'].includes(job.status);

  return (
    <div className={`upload-link-box q-row ${sent ? 'active' : 'expired'}${open ? ' open' : ''}`}>
      <div className="q-head">
        <button
          type="button"
          className="q-toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`q-caret${open ? ' open' : ''}`} aria-hidden="true">›</span>
          <span className="ulb-chip">Clarification queries</span>
          <span className="muted">{draft.length} drafted from the documents</span>
        </button>
        {/*
          Both actions retire permanently once the queries have gone out. The app
          sends these ONCE; anything further is a conversation, and that happens in
          Outlook where the valuer can answer in their own words. Re-sending would
          also mint a second answer link and orphan the first, so the client could be
          looking at a form whose answers no longer reach the job.
        */}
        <span className="q-actions">
          {/*
            State, not instructions. The paragraph that was here explained the Outlook
            workflow every single time the panel rendered, long after the valuer had
            read it once.
          */}
          {sent ? (
            <span className="q-sent-note">
              {latestSet?.answeredAt
                ? `Answered ${new Date(latestSet.answeredAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : job.queriesSentAt
                  ? `Sent ${new Date(job.queriesSentAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
                  : 'Sent'}
            </span>
          ) : (
            <>
              <button type="button" className="btn-secondary btn-mini-2" onClick={() => setEditing((v) => !v)}>
                {editing ? 'Done editing' : 'Edit the email'}
              </button>
              {!editing ? (
                <button className="btn" disabled={busy !== null} onClick={send}>
                  {busy === 'queries' ? 'Sending.' : 'Send to client'}
                </button>
              ) : null}
            </>
          )}
        </span>
      </div>

      {open && !editing ? (
        <>
          {answeredNote ? (
            <div className="q-note">
              <strong>The client replied in their own words:</strong>
              <p>{answeredNote}</p>
            </div>
          ) : null}
          <ol className="q-list">
            {draft.map((q, i) => (
              <li key={i}>
                {q.area ? <span className="q-area">{q.area}</span> : null}
                {q.query}
              </li>
            ))}
          </ol>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {!sent
              ? `Sending emails these queries to ${job.contact.name} together with a link to answer them in the browser. Press Edit the email to change the wording or attach a document first.`
              : latestSet?.answeredAt
                ? 'Answered. Anything further happens in Outlook and appears on the email thread below.'
                : 'Any further exchange happens in Outlook and appears on the email thread below.'}
          </p>
        </>
      ) : null}

      {open && editing ? (
      <>
      <p className="muted" style={{ marginTop: 0 }}>
        Composed from the draft queries. The link for the client to answer online is
        added when it sends.
      </p>
      <div className="field">
        <label>Subject</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div className="rte-toolbar">
        <button type="button" title="Bold" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}><b>B</b></button>
        <button type="button" title="Italic" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}><i>I</i></button>
        <button type="button" title="Underline" onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}><u>U</u></button>
        <span className="rte-sep" />
        <button type="button" title="Bulleted list" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>• List</button>
        <button type="button" title="Numbered list" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1. List</button>
      </div>
      <div ref={editorRef} className="rte-editor" contentEditable suppressContentEditableWarning />
      <div className="rte-attach">
        <label className="btn-secondary btn-mini-2" style={{ cursor: 'pointer' }}>
          Attach files
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
              e.target.value = '';
            }}
          />
        </label>
        {files.map((f, i) => (
          <span key={i} className="attach-chip">
            {f.name}
            <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}>×</button>
          </span>
        ))}
      </div>
      {attachable.length > 0 ? (
        <div className="rte-docs">
          <span className="muted" style={{ fontSize: '0.82rem' }}>Attach job documents:</span>
          <div className="doc-checklist">
            {attachable.map((d) => (
              <label key={d.docId} className="doc-check">
                <input
                  type="checkbox"
                  checked={docIds.has(d.docId)}
                  onChange={(e) =>
                    setDocIds((prev) => {
                      const n = new Set(prev);
                      if (e.target.checked) n.add(d.docId);
                      else n.delete(d.docId);
                      return n;
                    })
                  }
                />
                {d.filename}
              </label>
            ))}
          </div>
        </div>
      ) : null}
      <div className="btn-row" style={{ marginTop: '0.9rem' }}>
        <button onClick={send} disabled={busy !== null}>
          {busy === 'queries' ? 'Sending.' : 'Send email'}
        </button>
      </div>
      </>
      ) : null}
    </div>
  );
}

// Status-driven guidance so a staff member always knows the state and the next
// action. `wait: true` means the ball is in the client's court (no staff step).
/**
 * What the job is still waiting on, in the extraction's own words.
 *
 * The app has always worked this out and written it to the job; it just never put it
 * on screen. Two test jobs on 2026-09-11 sat at Collect information with no
 * explanation, and the reasons were precise and sitting in the record: one client had
 * sent FY2019-21 against an FY24-26 engagement, the other had no full-year FY2026.
 * Showing this is the difference between "the app is stuck" and "the client sent the
 * wrong years".
 */
function OutstandingPanel({
  job,
  onEditYears,
  onChanged,
}: {
  job: Job;
  onEditYears: () => void;
  onChanged: () => void | Promise<void>;
}) {
  const missing = job.workpaperMissing ?? [];
  // Outstanding items stay actionable until the report is issued.
  //
  // This used to hide as soon as the job left AWAITING_DOCS / DOCS_RECEIVED, on the
  // reasoning that anything after collection was historical. It is not. A job moves
  // to REVIEW the moment llm-worker drafts its queries, which happens automatically
  // and says nothing about whether the documents are complete. Peppermint Dental
  // (2026-09-13) reached REVIEW with no workbook at all and four real outstanding
  // items, including two missing FY2026 statements and an entity-name mismatch, and
  // every way out of it, chase the client, change the years, proceed anyway,
  // disappeared from the page. The valuer was left with a job that had silently
  // stopped and no control that addressed it.
  //
  // Once the report has been issued they genuinely are history, so that is where the
  // line belongs.
  const collecting = !['DELIVERED'].includes(job.status);
  const [composing, setComposing] = useState(false);
  const [subject, setSubject] = useState(`The documents we still need for ${job.entityName}`);
  const [busy, setBusy] = useState<null | 'chase' | 'accept'>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Seed the draft when the composer opens, not on mount: the editor is not in the
  // DOM until then, which is the same trap the clarification email fell into.
  useEffect(() => {
    if (composing && editorRef.current && !editorRef.current.innerHTML.trim()) {
      editorRef.current.innerHTML = chaseDraftHtml(job, missing);
    }
  }, [composing, job, missing]);

  if (!missing.length || !collecting) return null;

  async function send() {
    setBusy('chase');
    setError(null);
    setMsg(null);
    try {
      const html = editorRef.current?.innerHTML || chaseDraftHtml(job, missing);
      await resolveDocsGap(job.jobId, { action: 'chase', subject, html });
      setComposing(false);
      setMsg('Sent. The app will follow up on the usual cadence until the client responds.');
      await onChanged();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function accept() {
    setBusy('accept');
    setError(null);
    setMsg(null);
    try {
      await resolveDocsGap(job.jobId, { action: 'accept' });
      setMsg('Accepted. The workpaper is being built on the years the client actually supplied.');
      await onChanged();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="outstanding-panel">
      <div className="outstanding-head">
        <strong>Still needed before this job can move to Review</strong>
        {job.workpaperCheckedAt ? (
          <span className="outstanding-when">checked {new Date(job.workpaperCheckedAt).toLocaleString()}</span>
        ) : null}
      </div>
      <ul>
        {missing.map((m, i) => (
          <li key={i}>{m}</li>
        ))}
      </ul>
      {error ? <ErrorBanner message={error} /> : null}
      {msg ? <div className="notice">{msg}</div> : null}

      {composing ? (
        <div className="chase-compose">
          <label className="field">Subject
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </label>
          <div className="editor" contentEditable ref={editorRef} suppressContentEditableWarning />
          <p className="outstanding-foot">
            The client&rsquo;s existing upload link is added when this sends, so they can
            add the missing files to the same job.
          </p>
          <div className="btn-row">
            <button onClick={send} disabled={busy !== null}>
              {busy === 'chase' ? 'Sending.' : 'Send to client'}
            </button>
            <button className="btn-secondary" onClick={() => setComposing(false)} disabled={busy !== null}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {/*
            Three ways out, matching what actually goes wrong. Without these the
            panel explains the problem and offers nothing, which is where both test
            jobs sat on 2026-09-11.
          */}
          <div className="btn-row">
            <button onClick={() => setComposing(true)} disabled={busy !== null}>
              Ask the client for this
            </button>
            <button className="btn-secondary" onClick={onEditYears} disabled={busy !== null}>
              Change the financial years
            </button>
            <button
              className="btn-secondary"
              onClick={accept}
              disabled={busy !== null}
              title="Build the workpaper on the years the client actually supplied. Recorded against your name."
            >
              {busy === 'accept' ? 'Working.' : 'Proceed anyway'}
            </button>
          </div>
          <p className="outstanding-foot">
            <strong>Ask the client</strong> if they sent the wrong files.{' '}
            <strong>Change the financial years</strong> if the documents are right and the
            engagement is wrong. <strong>Proceed anyway</strong> if you judge what they sent
            is enough; the workpaper is then built on the years supplied, and the decision is
            recorded against your name.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * The chase email, composed from the extraction's own words.
 *
 * Those sentences are already written to the client ("the three files supplied cover
 * FY2019, FY2020 and FY2021, none of which are required years"), which is exactly what
 * a chase needs to say and precisely what the deterministic requirements check cannot
 * work out. The valuer reads and edits before it goes.
 */
function chaseDraftHtml(job: Job, missing: string[]): string {
  const items = missing.map((m) => `<li>${escHtml(m)}</li>`).join('');
  return [
    `<p>Hi ${escHtml(firstName(job.contact.name))},</p>`,
    `<p>Thank you for the documents you have sent through for ${escHtml(job.entityName)}. ` +
      'Having gone through them, there are a few things we still need before we can ' +
      'start the valuation:</p>',
    `<ul>${items}</ul>`,
    '<p>If any of this is not available, tell us what you do have and we will work ' +
      'out the best way forward.</p>',
    '<p>Kind regards,<br>WA Business Valuations</p>',
  ].join('');
}

function NextStep({ status }: { status: string }) {
  const map: Record<string, { text: string; wait?: boolean; done?: boolean }> = {
    DRAFT: { text: 'Job created. Review the details below, then Send quote when ready.' },
    QUOTE_SENT: { text: 'Quote sent. The client has been emailed a link to sign the proposal (eTOE). Once signed, the upload link and Xero invoice go out automatically.', wait: true },
    PROPOSAL_SIGNED: { text: 'Proposal signed. The 30-day Collect information link and the Xero invoice have been sent to the client.', wait: true },
    AWAITING_DOCS: { text: 'Waiting on the client. One link covers both halves of this step, the general information questionnaire and the documents; they can do them in either order. Uploads are checked twice daily (10:00 and 14:00 AWST), and once both are in the workpaper generates and the job moves to Review.', wait: true },
    DOCS_RECEIVED: { text: 'Documents received and being read. The workpaper and draft queries are generating now, this takes about a minute.', wait: true },
    REVIEW: { text: 'Ready for review: check the generated P&L and Balance Sheet and the draft clarification queries below, then send the queries.' },
    QUERIES_SENT: { text: 'Queries sent. Waiting for the client to answer them in the portal; you will be emailed when they submit.', wait: true },
    QUERIES_ANSWERED: { text: 'The client has answered every query. Review their answers, then build the report.', wait: false },
    REPORTING: { text: 'Generating the valuation report. The draft downloads for your review; use Generate & email to deliver it to the client.', wait: true },
    DELIVERED: { text: 'Engagement finalised. Issue your report to the client yourself; the app does not send it.', done: true },
    // legacy statuses on old jobs
    QUOTE_DRAFTED: { text: 'Quote drafted and ready. Review it, then click Send quote to email the client.' },
    QUERIES_DRAFTED: { text: 'Draft clarification queries are ready below. Review and edit them, then click Send queries.' },
    CLOSED: { text: 'This engagement is complete.', wait: true },
  };
  const step = map[status];
  if (!step) return null;
  const label = step.done ? 'Done' : step.wait ? 'Waiting' : 'Next step';
  return (
    <div className={`next-step ${step.wait || step.done ? 'waiting' : 'action'}`}>
      <span className="next-step-label">{label}</span>
      {step.text}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className="field" style={{ marginBottom: '0.5rem' }}>
      <label>{label}</label>
      <div>{value && value.length > 0 && value !== 'unknown' ? value : <span className="muted">Not recorded</span>}</div>
    </div>
  );
}

function downloadPdf(base64: string, filename: string) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Valuation report panel: the valuer enters their inputs; the app assembles the
// report PDF (it does the arithmetic, the valuer owns the judgement). Generate a
// DRAFT to review (downloads immediately), then generate & deliver to the client.
// Surfaces the deterministic integrity check on the AI-extracted figures (pentest
// HIGH-2) so the valuer verifies anything the automation could not confirm against
// the source documents before relying on the workpaper.
/**
 * The app's verdict on the documents above it.
 *
 * `inline` drops the card chrome so it can sit inside the Documents panel: it is
 * a statement about those files, not a separate object, and it used to render
 * three panels lower where nobody connected the two.
 */
function WorkpaperIntegrityBanner({ job, inline = false }: { job: Job; inline?: boolean }) {
  const integ = job.workpaperData?.integrity;
  if (!integ) return null;
  const box = inline ? 'integrity-inline' : 'card';
  if (integ.ok) {
    return (
      <div className={box} style={{ borderLeft: '4px solid #16a34a' }}>
        <strong>Workpaper integrity check passed.</strong> Every extracted figure was found
        in the source documents and the balance sheet reconciles. Confirm against the
        documents as usual.
      </div>
    );
  }
  return (
    <div className={box} style={{ borderLeft: '4px solid #d97706', background: '#fffbeb' }}>
      <strong>⚠ Verify these figures before relying on the workpaper.</strong>
      <p style={{ margin: '8px 0 4px' }}>
        The automated check could not confirm some AI-extracted figures against the source
        documents. Check them in the source statements before generating the report.
      </p>
      {integ.untraceableCount > 0 ? (
        <>
          <div style={{ fontWeight: 600, marginTop: 8 }}>
            {integ.untraceableCount} figure(s) not found in the source text:
          </div>
          <ul style={{ margin: '4px 0' }}>
            {integ.untraceable.slice(0, 12).map((u, i) => (
              <li key={i}>
                {u.section} · {u.label} · {u.year}: {u.value.toLocaleString()}
              </li>
            ))}
            {integ.untraceable.length > 12 ? <li>…and {integ.untraceable.length - 12} more</li> : null}
          </ul>
        </>
      ) : null}
      {integ.balance.length > 0 ? (
        <>
          <div style={{ fontWeight: 600, marginTop: 8 }}>Balance sheet did not reconcile:</div>
          <ul style={{ margin: '4px 0' }}>
            {integ.balance.map((b, i) => (
              <li key={i}>
                {b.year} · {b.identity}: {b.lhs.toLocaleString()} vs {b.rhs.toLocaleString()} (off by{' '}
                {b.diff.toLocaleString()})
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * The valuation date, shown the way the report prints it.
 *
 * A native date input renders in the BROWSER's locale, so an Australian valuer saw
 * "09/13/2026" for 13 September. On the cover of a valuation that is not a papercut,
 * it is a date somebody could read as 9 December. The native picker is still what
 * sets the value, because a hand-typed date is where "30/6/26" comes from, but the
 * field itself always reads "13 September 2026".
 */
function ValuationDateField({
  value,
  onChange,
}: {
  value?: string;
  onChange: (display: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const open = () => {
    const el = ref.current;
    if (!el) return;
    // showPicker is the supported way; falling back to a click keeps older browsers
    // working rather than leaving a field that cannot be set at all.
    if (typeof el.showPicker === 'function') el.showPicker();
    else el.click();
  };
  return (
    <label className="field">
      Valuation date
      <button type="button" className="date-display" onClick={open}>
        <span className={value ? undefined : 'muted'}>{value || 'Choose a date'}</span>
        <span aria-hidden="true">📅</span>
      </button>
      <input
        ref={ref}
        type="date"
        className="date-hidden"
        tabIndex={-1}
        aria-label="Valuation date"
        value={isoFromDisplay(value)}
        onChange={(e) => onChange(displayFromIso(e.target.value))}
      />
    </label>
  );
}

/** Money for a bare cell input: raw while focused, formatted when not. */
function moneyCell(v: number | undefined, focused: boolean): string {
  if (v == null) return '';
  if (focused) return String(v);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
}

/**
 * A money input that reads as money.
 *
 * Same behaviour as the client questionnaire: formatted when you are not in it, raw
 * while you are, because formatting the live value fights the caret. Negative is
 * allowed and meaningful here, since net debt below zero is net cash.
 */
function MoneyField({
  label,
  value,
  onChange,
  className,
  hint,
}: {
  label: string;
  value?: number;
  onChange: (v: number | undefined) => void;
  className?: string;
  hint?: string;
}) {
  const [focused, setFocused] = useState(false);
  const shown = focused
    ? value == null
      ? ''
      : String(value)
    : value == null
      ? ''
      : `${value < 0 ? '-' : ''}$${Math.abs(value).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;
  return (
    <label className={`field${className ? ` ${className}` : ''}`}>
      {label}
      <input
        inputMode="decimal"
        className="money"
        value={shown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^0-9.\-]/g, '');
          const n = cleaned.trim() === '' ? undefined : Number(cleaned);
          onChange(Number.isFinite(n as number) ? n : undefined);
        }}
      />
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

/**
 * The valuation date, as a native picker rather than free text.
 *
 * Stored and printed as "30 June 2026" because that is what the report renders and
 * what a valuer reads. The input needs ISO, so convert at the boundary rather than
 * changing what the PDF prints. A blank field opens the picker on today.
 */
function isoFromDisplay(v?: string): string {
  const t = (v ?? '').trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function displayFromIso(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function ReportPanel({ job, onChanged }: { job: Job; onChanged: () => void | Promise<void> }) {
  const v = job.valuation ?? {};
  const [f, setF] = useState<ValuationInputs>({
    valuationDate: v.valuationDate ?? job.valuationDate ?? '',
    basisOfValue: v.basisOfValue ?? 'Fair Market Value',
    method: v.method ?? 'Capitalisation of Future Maintainable Earnings',
    earningsBasis: v.earningsBasis ?? 'EBITDA',
    reportedEarnings: v.reportedEarnings,
    maintainableEarnings: v.maintainableEarnings,
    multiple: v.multiple,
    netDebt: v.netDebt,
    discountPct: v.discountPct,
    concludedLow: v.concludedLow,
    concludedHigh: v.concludedHigh,
    preparedBy: v.preparedBy ?? 'WA Business Valuations',
    narrativeOverview: v.narrativeOverview ?? '',
  });
  const [busy, setBusy] = useState<null | 'workpaper' | 'report' | 'draft'>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [reportedFocused, setReportedFocused] = useState(false);
  /**
   * Keep the valuer's figures without making them press anything.
   *
   * Nothing here was saved until a build button was pressed, so typing a figure and
   * navigating away lost it. A rebuild is not the right moment to save either: it
   * takes three seconds and writes a new workbook, which is not what you want each
   * time somebody tabs out of a field. This patches the job on its own, debounced,
   * and touches nothing else.
   */
  const saved = useRef(JSON.stringify(v));
  useEffect(() => {
    const next = JSON.stringify(cleanValuation());
    if (next === saved.current) return;
    const t = setTimeout(() => {
      saved.current = next;
      updateJob(job.jobId, { valuation: JSON.parse(next) }).catch(() => {
        // A failed autosave must not interrupt the valuer mid-figure. The build
        // buttons save explicitly and will surface a real error if it matters.
        saved.current = '';
      });
    }, 1200);
    return () => clearTimeout(t);
  });

  const [addBackFocus, setAddBackFocus] = useState<number | null>(null);

  /**
   * Refuse loudly, where the person is looking.
   *
   * The error banner sits at the top of the panel and the buttons are at the bottom.
   * On a form this tall that means pressing a button produced no visible response at
   * all: Matt pressed Preview draft report, the guard correctly refused, and the
   * explanation was a screen and a half above him. An error nobody sees is a button
   * that does nothing.
   */
  function refuse(message: string) {
    setMsg(null);
    setError(message);
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  // Reported plus every add-back, which is exactly what the PDF computes. Undefined
  // until there is something to compute from, so the row reads as blank rather than $0.
  const computedFme =
    f.reportedEarnings == null
      ? undefined
      : f.reportedEarnings + (f.addBacks ?? []).reduce((t, a) => t + (a.amount ?? 0), 0);
  // The same arithmetic the report performs, so the panel can say whether the range
  // you typed agrees with it. Page 1 prints the range and page 4 prints the build-up,
  // and nothing has ever reconciled the two: they could disagree by any amount and
  // the first person to notice would be the client with a calculator.
  const assessedEquity = (() => {
    const fme = f.maintainableEarnings;
    if (fme == null || f.multiple == null || f.netDebt == null) return undefined;
    const pre = fme * f.multiple - f.netDebt;
    return pre - (pre * (f.discountPct ?? 0)) / 100;
  })();
  const rangeState = (() => {
    if (assessedEquity == null || f.concludedLow == null || f.concludedHigh == null) return null;
    if (f.concludedLow > f.concludedHigh) return { ok: false, text: 'The low end of your range is above the high end.' };
    const inside = assessedEquity >= f.concludedLow && assessedEquity <= f.concludedHigh;
    return {
      ok: inside,
      text: inside
        ? `Assessed equity value ${money(assessedEquity)} sits inside your range.`
        : `Assessed equity value ${money(assessedEquity)} is outside your stated range of ${money(f.concludedLow)} to ${money(f.concludedHigh)}.`,
    };
  })();

  const [fmeOverridden, setFmeOverridden] = useState(
    // A job that already carries a maintainable figure with no add-backs behind it was
    // entered by hand, so keep it editable rather than silently recomputing over it.
    () => v.maintainableEarnings != null && !(v.addBacks ?? []).length,
  );

  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Numeric fields keep the text you typed as well as the parsed number.
  //
  // Without this you cannot enter a decimal at all: typing "0.75" passes through
  // "0", then "0.", and Number("0.") is 0, so the field re-renders as "0" and eats
  // the point. A capitalisation multiple of 0.75 or 3.25 was therefore impossible
  // to enter, which is what Matt hit on 2026-09-11.
  const [typed, setTyped] = useState<Partial<Record<keyof ValuationInputs, string>>>({});

  const set = (k: keyof ValuationInputs) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const raw = e.target.value;
    const numeric: (keyof ValuationInputs)[] = ['reportedEarnings', 'maintainableEarnings', 'multiple', 'netDebt', 'discountPct', 'concludedLow', 'concludedHigh'];
    if (!numeric.includes(k)) {
      setF((p) => ({ ...p, [k]: raw }));
      return;
    }
    setTyped((p) => ({ ...p, [k]: raw }));
    // Strip formatting before parsing. Accountants paste "1,164,195.21" and "$53,057"
    // straight out of the workbook, and Number() returns NaN for both. The old code
    // then kept the PREVIOUS value while the field went on displaying what was
    // pasted: the field looked filled and the app held nothing, which is how an empty
    // valuation reached the report builder.
    const cleaned = raw.replace(/[^0-9.\-]/g, '');
    const n = cleaned.trim() === '' ? undefined : Number(cleaned);
    setF((p) => ({ ...p, [k]: Number.isFinite(n as number) ? n : undefined }));
  };

  /**
   * What must be present before a report can be issued.
   *
   * Without this an empty form produced a finalised PDF reading $0, with no DRAFT
   * stamp, and moved the job to DELIVERED: report-build guards document completeness
   * but validates no valuation input, and the Python generator turns a missing figure
   * into 0.0. A $0 valuation carrying the practice's name is the worst thing this app
   * could emit.
   */
  function missingForReport(): string[] {
    const need: [unknown, string][] = [
      [f.valuationDate?.trim(), 'valuation date'],
      [f.maintainableEarnings, 'future maintainable earnings'],
      [f.multiple, 'capitalisation multiple'],
      [f.concludedLow, 'concluded value (low)'],
      [f.concludedHigh, 'concluded value (high)'],
      [f.preparedBy?.trim(), 'prepared by'],
    ];
    const out = need.filter(([v]) => v === undefined || v === null || v === '').map(([, l]) => l);
    // Net debt of zero is a real answer; not having touched the field is not.
    if (f.netDebt === undefined || f.netDebt === null) out.push('net debt (enter 0 if there is none)');
    if (typeof f.multiple === 'number' && f.multiple <= 0) out.push('a capitalisation multiple above zero');
    if (
      typeof f.concludedLow === 'number' &&
      typeof f.concludedHigh === 'number' &&
      f.concludedLow > f.concludedHigh
    ) {
      out.push('a concluded range where the low is not above the high');
    }
    return out;
  }

  // The form read job.valuation once, at mount, and never again. After a workpaper
  // build the job refetches and the panel went on holding whatever was in the browser,
  // so two tabs open, or a job reassigned mid-edit, meant the last save silently won.
  // Only resync when the server has something the form does not.
  useEffect(() => {
    if (!v.generatedAt) return;
    setF((p) => (p.generatedAt === v.generatedAt ? p : { ...p, ...v }));
  }, [v]);

  // The stored figure follows the computation unless the valuer has taken it over.
  useEffect(() => {
    if (!fmeOverridden && computedFme !== f.maintainableEarnings) {
      setF((p) => ({ ...p, maintainableEarnings: computedFme }));
    }
  }, [computedFme, fmeOverridden, f.maintainableEarnings]);

  /** Strip half-typed add-back rows before anything is stored or printed. */
  function cleanValuation(): ValuationInputs {
    return {
      ...f,
      addBacks: (f.addBacks ?? []).filter((a) => a.label?.trim() && typeof a.amount === 'number'),
    };
  }

  async function generate(deliver: boolean) {
    // A draft is for reading while you work, so it asks only for enough to produce a
    // number worth looking at. Finalising asks for everything, because that PDF
    // carries the practice's name with no watermark on it.
    const missing = deliver
      ? missingForReport()
      : [
          ...(f.maintainableEarnings == null ? ['future maintainable earnings'] : []),
          ...(f.multiple == null || f.multiple <= 0 ? ['a capitalisation multiple above zero'] : []),
        ];
    if (missing.length) {
      refuse(
        deliver
          ? `Cannot finalise yet. Still needed: ${missing.join(', ')}.`
          : `Cannot build a draft yet. Still needed: ${missing.join(', ')}.`,
      );
      return;
    }
    setBusy(deliver ? 'report' : 'draft');
    setError(null);
    setMsg(null);
    try {
      const res = await buildReport(job.jobId, { valuation: cleanValuation(), deliver });
      downloadPdf(res.pdfBase64, res.filename);
      await onChanged();
      setMsg(
        deliver
          ? 'Report finalised and downloaded. The engagement is marked delivered. The client has NOT been emailed: attach it to your own covering email.'
          : 'Draft downloaded, watermarked. Nothing has been finalised.',
      );
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function buildWorkbook() {
    setBusy('workpaper');
    setError(null);
    setMsg(null);
    try {
      await buildWorkpaper(job.jobId, { valuation: cleanValuation() });
      // Pull the job again so Generated documents shows the file that was just built.
      // Without this the new workbook only appeared on a manual browser refresh, and a
      // refresh is also what emptied the form, so the valuer paid for the update twice.
      await onChanged();
      // Name what was saved. Pressing this on an empty form used to report success
      // without hinting that it had stored nothing at all.
      const entered = missingForReport();
      setMsg(
        entered.length
          ? `Workpaper rebuilt from the extracted figures. No valuation inputs were saved: ${entered.join(', ')} ${entered.length === 1 ? 'is' : 'are'} still blank.`
          : `Workpaper rebuilt, carrying FME ${money(f.maintainableEarnings)} at ${f.multiple}x. It is the newest Valuation Workpaper.xlsx under Generated documents.`,
      );
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }

  // Show what was typed while the field is being edited, so "1.00" and "0.7" survive
  // until focus leaves; fall back to the stored number otherwise.
  const num = (k: keyof ValuationInputs) => {
    const t = typed[k];
    if (t !== undefined) return t;
    const n = f[k];
    return typeof n === 'number' ? String(n) : '';
  };

  return (
    <div className="card" ref={panelRef}>
      <h3>Valuation report</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Enter your valuation inputs. The app does the arithmetic; you own the method, multiple and
        concluded value. This produces a working summary, not the signed report you issue: the app
        does not email it to the client.
      </p>
      {error ? <ErrorBanner message={error} /> : null}
      {msg ? <div className="notice">{msg}</div> : null}
      {/*
        Four columns. A multiple of 3.25 and a discount of 0 do not need half the page
        each, and two columns made this nine rows deep. The long text fields opt back
        out with span-2.
      */}
      <div className="grid-4">
        <ValuationDateField
          value={f.valuationDate}
          onChange={(d) => setF((p) => ({ ...p, valuationDate: d }))}
        />
        <label className="field">Earnings basis
          <select value={f.earningsBasis ?? 'EBITDA'} onChange={set('earningsBasis')}>
            <option>EBITDA</option><option>EBIT</option><option>NPBT</option>
          </select>
        </label>
        <label className="field span-2">Method
          <input value={f.method ?? ''} onChange={set('method')} />
        </label>

        <label className="field">Capitalisation multiple (x)
          <input
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            placeholder="e.g. 3.25"
            value={num('multiple')}
            onChange={set('multiple')}
          />
        </label>
        <label className="field">Discount (%)
          <input inputMode="decimal" value={num('discountPct')} onChange={set('discountPct')} />
        </label>
        <MoneyField
          label="Net debt (negative = net cash)"
          value={f.netDebt}
          onChange={(v) => setF((p) => ({ ...p, netDebt: v }))}
        />
        <label className="field">Basis of value
          <input value={f.basisOfValue ?? ''} onChange={set('basisOfValue')} />
        </label>

        <MoneyField
          label="Concluded value, low"
          value={f.concludedLow}
          onChange={(v) => setF((p) => ({ ...p, concludedLow: v }))}
        />
        <MoneyField
          label="Concluded value, high"
          value={f.concludedHigh}
          onChange={(v) => setF((p) => ({ ...p, concludedHigh: v }))}
        />
        <label className="field span-2">Prepared by
          <input value={f.preparedBy ?? ''} onChange={set('preparedBy')} />
        </label>
      </div>
      {rangeState ? (
        <p className={rangeState.ok ? 'field-hint' : 'range-warn'}>{rangeState.text}</p>
      ) : null}
      {/*
        The normalisation, laid out the way the report prints it. The model and the
        PDF have carried addBacks since the report was first built: each one is
        itemised in section 5 and summed into maintainable earnings. There has simply
        never been anywhere to enter one, so the report showed a before and an after
        with nothing between them, and the client's own answer ("a market salary would
        be $220,000" against an actual $302,057) could not be disclosed at all.
      */}
      <div className="earnings-block">
        <h4>Earnings normalisation</h4>
        <div className="calc-row">
          <label htmlFor="reported-earnings">Reported earnings ({f.earningsBasis ?? 'EBITDA'})</label>
          <input
            id="reported-earnings"
            inputMode="decimal"
            className="money calc-input"
            value={moneyCell(f.reportedEarnings, reportedFocused)}
            onFocus={() => setReportedFocused(true)}
            onBlur={() => setReportedFocused(false)}
            onChange={(e) => {
              const c = e.target.value.replace(/[^0-9.\-]/g, '');
              const n = c.trim() === '' ? undefined : Number(c);
              setF((p) => ({ ...p, reportedEarnings: Number.isFinite(n as number) ? n : undefined }));
            }}
          />
        </div>

        {(f.addBacks ?? []).map((a, i) => (
          <div className="calc-row" key={i}>
            <input
              aria-label={`Add-back ${i + 1} description`}
              placeholder="Add back: what and why"
              value={a.label ?? ''}
              onChange={(e) =>
                setF((p) => ({
                  ...p,
                  addBacks: (p.addBacks ?? []).map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                }))
              }
            />
            <input
              aria-label={`Add-back ${i + 1} amount`}
              inputMode="numeric"
              className="money calc-input"
              value={moneyCell(a.amount, addBackFocus === i)}
              onFocus={() => setAddBackFocus(i)}
              onBlur={() => setAddBackFocus(null)}
              onChange={(e) => {
                const n = Number(e.target.value.replace(/[^0-9.\-]/g, ''));
                setF((p) => ({
                  ...p,
                  addBacks: (p.addBacks ?? []).map((x, j) =>
                    j === i ? { ...x, amount: Number.isFinite(n) ? n : undefined } : x,
                  ),
                }));
              }}
            />
            <button
              type="button"
              className="btn-secondary btn-mini-2"
              aria-label={`Remove add-back ${i + 1}`}
              onClick={() => setF((p) => ({ ...p, addBacks: (p.addBacks ?? []).filter((_, j) => j !== i) }))}
            >
              Remove
            </button>
          </div>
        ))}

        <div className="btn-row">
          <button
            type="button"
            className="btn-secondary btn-mini-2"
            onClick={() => setF((p) => ({ ...p, addBacks: [...(p.addBacks ?? []), { label: '', amount: undefined }] }))}
          >
            Add an add-back
          </button>
        </div>

        {/*
          Computed by default, because that is what the PDF already assumes, with an
          override for the valuer who has worked the figure out another way. Their
          number always wins; it is simply no longer the only way to get one.
        */}
        <div className="calc-row calc-total">
          <label htmlFor="fme">Future maintainable earnings</label>
          {fmeOverridden ? (
            <input
              id="fme"
              inputMode="numeric"
              className="money calc-input"
              value={num('maintainableEarnings')}
              onChange={set('maintainableEarnings')}
            />
          ) : (
            <span className="calc-value">{computedFme == null ? '—' : money(computedFme)}</span>
          )}
          <button
            type="button"
            className="btn-secondary btn-mini-2"
            onClick={() => {
              if (fmeOverridden) {
                setFmeOverridden(false);
                setTyped((p) => ({ ...p, maintainableEarnings: undefined }));
                setF((p) => ({ ...p, maintainableEarnings: computedFme }));
              } else {
                setFmeOverridden(true);
              }
            }}
          >
            {fmeOverridden ? 'Use the computed figure' : 'Override'}
          </button>
        </div>
      </div>

      <label className="field">Business overview (optional)
        <textarea rows={3} value={f.narrativeOverview ?? ''} onChange={set('narrativeOverview')} />
      </label>
      <div className="btn-row" style={{ marginTop: '0.5rem' }}>
        {/*
          The workbook is WABV's actual working document, so it comes first. It is
          generated once at extraction time, before any of this exists; this rebuilds
          it carrying the figures above, the client's questionnaire and their answers
          to the queries.
        */}
        <button onClick={buildWorkbook} disabled={busy !== null}>
          {busy === 'workpaper' ? 'Building.' : 'Build workpaper (XLSX)'}
        </button>
        {/*
          A draft you can read without committing to it. The backend has stamped DRAFT
          on the filename and the page since 2026-09-08 and the SPA never offered the
          path, so the only way to see your own report was to finalise the engagement.
          Needs enough figures to be worth reading, not every field.
        */}
        <button
          className="btn-secondary"
          onClick={() => generate(false)}
          disabled={busy !== null}
          title="Downloads a watermarked draft. Nothing is finalised and the client is not emailed."
        >
          {busy === 'draft' ? 'Building.' : 'Preview draft report'}
        </button>
        {/*
          Not "email to client". WABV's real deliverable is a signed APES 225 report
          produced in Word; this PDF is a scaffold. Sending it under the practice's
          name was the most exposed thing in the pipeline. See
          docs/report-gap-2026-09-08.md.

          Set apart from the two reversible actions above, and deliberately NOT the
          loudest button on the panel: it is the one that changes the engagement's
          state, so it should take a decision rather than a reflex.
        */}
        <button
          className="btn-finalise"
          onClick={() => {
            if (
              !window.confirm(
                `Finalise ${job.entityName}?\n\nThis downloads the report without the draft watermark and marks the engagement delivered. The client is not emailed.`,
              )
            ) {
              return;
            }
            generate(true);
          }}
          disabled={busy !== null}
          title="Downloads the report without the draft watermark and marks the engagement finalised. The client is not emailed."
        >
          {busy === 'report' ? 'Finalising.' : 'Finalise and issue'}
        </button>
      </div>
      <p className="muted" style={{ fontSize: '0.8rem', marginBottom: 0 }}>
        Each add-back is itemised by name in the report's normalisation section. Future
        maintainable earnings is reported earnings plus the add-backs unless you override it.
      </p>
    </div>
  );
}
